import type {Repository} from '@github-ui/current-repository'
import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {updateSearchParams} from '@github-ui/history'
import {useExternalAnchor, type PropsWithPartialAnchor} from '@github-ui/react-core/react-partial-anchor'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {ActionMenu, AnchoredOverlay, type AnchoredOverlayProps, type ButtonProps} from '@primer/react'
import {useCallback, useEffect, useRef, useState, type MutableRefObject} from 'react'
import {CCAPopover} from './CCAPopover'
import {GlobalCopilotOverlay} from './GlobalCopilotOverlay'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useLocalStorageCache} from '@github-ui/agent-sessions/hooks/useLocalStorageCache'
import {
  fetchPullRequests,
  getSortedResourceIds,
  PR_MAP_STORAGE_KEY,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/agent-sessions/hooks/usePullRequests'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {agentTasksQueryKey, fetchTasks, TASKS_STORAGE_KEY} from '@github-ui/agent-sessions/hooks/useTasks'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {IdToPullRequestMap} from '@github-ui/agent-sessions/types/pull'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {BaseTask} from '@github-ui/agent-sessions/types/task'
import {
  copilotChatPayloadQueryOptions,
  useCopilotChatPayloadQuery,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayloadQuery'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {useQueryClient} from '@github-ui/react-query'
import {AgentIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

export type GlobalAgentsMenuProps = {
  repository: Repository | null
  popoverAvailable?: boolean
  agentsButtonId?: string
  anchorProps?: ButtonProps
}

type ActionMenuOverlayProps = {
  anchorRef: MutableRefObject<HTMLElement | null>
  open: boolean
  setOpen: (open: boolean) => void
  renderAnchor: AnchoredOverlayProps['renderAnchor']
}

// Custom event name for toggling the mission control overlay
export const TOGGLE_COPILOT_OVERLAY = 'toggle-copilot-overlay'

export function GlobalAgentsMenuWithAnchor(props: GlobalAgentsMenuProps) {
  const {anchorProps, ...rest} = props
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)

  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
      <GlobalAgentsMenuOverlay
        {...rest}
        anchorRef={anchorRef}
        open={open}
        setOpen={setOpen}
        renderAnchor={renderAnchorProps => (
          <ActionMenu.Button
            {...renderAnchorProps}
            {...anchorProps}
            leadingVisual={AgentIcon}
            ref={anchorRef}
            className={clsx(renderAnchorProps.className, anchorProps?.className)}
            aria-label="Open agents panel"
          >
            {''}
          </ActionMenu.Button>
        )}
      />
    </ActionMenu>
  )
}

export function GlobalAgentsMenu(props: PropsWithPartialAnchor<GlobalAgentsMenuProps>) {
  const {reactPartialAnchor, ...rest} = props
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(reactPartialAnchor)

  return <GlobalAgentsMenuOverlay {...rest} anchorRef={anchorRef} open={open} setOpen={setOpen} renderAnchor={null} />
}

function GlobalAgentsMenuOverlay(props: GlobalAgentsMenuProps & ActionMenuOverlayProps) {
  const {setValue: setCachedTasks} = useLocalStorageCache<BaseTask[]>(TASKS_STORAGE_KEY)
  const {setValue: setCachedPRMap} = useLocalStorageCache<IdToPullRequestMap>(PR_MAP_STORAGE_KEY)
  const queryClient = useQueryClient()
  const [hasPrefetched, setHasPrefetched] = useState(false)
  const {anchorRef, open, setOpen} = props
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {repository, popoverAvailable, agentsButtonId} = props
  const handleClose = () => {
    setOpen(false)
  }

  const [showPopover, setShowPopover] = useState(popoverAvailable || false)
  const dismissCCAPopover = useCallback(async () => {
    setShowPopover(false)
    await verifiedFetch('/settings/dismiss-notice/copilot_coding_agent_entry_point_popover', {method: 'POST'})
  }, [setShowPopover])

  useEffect(() => {
    if (open && showPopover) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      dismissCCAPopover()
    }
  }, [open, dismissCCAPopover, showPopover])

  useEffect(() => {
    if (open) {
      sendClickAnalyticsEvent({
        category: 'copilot-coding-agent-global-dropdown',
        action: 'open',
      })
    }
  }, [open, sendClickAnalyticsEvent])

  // Listen for the custom keyboard event to open the copilot overlay
  useEffect(() => {
    const handleToggleCopilotOverlay = () => {
      // Until Primer React Tooltip bug is fixed, temporarily hide zombie tooltip when opening overlay to prevent dual tooltips
      // https://github.com/github/primer/issues/5518
      const tooltip = anchorRef.current?.nextElementSibling
      if (!open && tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.setAttribute('style', 'display: none')
      }
      setOpen(!open)
    }

    document.addEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    return () => {
      document.removeEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    }
  }, [setOpen, open, anchorRef])

  // Check URL parameter to auto-open agents panel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('open_agents_panel') === '1' && !open) {
      setOpen(true)
      // Clean up the URL parameter to avoid reopening on refresh
      urlParams.delete('open_agents_panel')
      updateSearchParams(urlParams)
    }
  }, [open, setOpen])

  // Listen for button hover to prefetch tasks and pull requests
  useEffect(() => {
    // Don't add listener if already prefetched
    if (hasPrefetched) return

    const prefetchTasksAndPullRequests = async () => {
      try {
        // First prefetch tasks
        await queryClient.prefetchQuery({
          // eslint-disable-next-line @tanstack/query/exhaustive-deps
          queryKey: agentTasksQueryKey,
          queryFn: async () => await fetchTasks({queryClient, setCachedTasks}),
        })

        // Get the cached tasks to prefetch pull requests
        const cachedTasks = queryClient.getQueryData<BaseTask[]>(agentTasksQueryKey)
        if (cachedTasks && cachedTasks.length > 0) {
          const tasksWithResourceIds = cachedTasks.filter(task => task.resource_id !== undefined)
          if (tasksWithResourceIds.length > 0) {
            const pullRequestIds = getSortedResourceIds(tasksWithResourceIds)
            await queryClient.prefetchQuery({
              // eslint-disable-next-line @tanstack/query/exhaustive-deps
              queryKey: ['agent-sessions-pull-requests', pullRequestIds],
              queryFn: async () => await fetchPullRequests(tasksWithResourceIds, setCachedPRMap),
            })
          }
        }

        setHasPrefetched(true)
      } catch {
        // Silently handle prefetch errors so they don't block the UI
      }
    }

    const buttonElement = document.getElementById(agentsButtonId ?? 'global-copilot-agent-button')
    if (!buttonElement) return

    buttonElement.addEventListener('mouseenter', prefetchTasksAndPullRequests)
    buttonElement.addEventListener('focus', prefetchTasksAndPullRequests)

    return () => {
      buttonElement.removeEventListener('mouseenter', prefetchTasksAndPullRequests)
      buttonElement.removeEventListener('focus', prefetchTasksAndPullRequests)
    }
  }, [hasPrefetched, queryClient, setCachedTasks, setCachedPRMap, agentsButtonId])

  useEffect(() => {
    const buttonElement = document.getElementById(agentsButtonId ?? 'global-copilot-agent-button')
    if (!buttonElement) return

    const handlePrefetch = () => {
      queryClient.prefetchQuery(copilotChatPayloadQueryOptions)
    }

    buttonElement.addEventListener('mouseenter', handlePrefetch, {once: true})
    buttonElement.addEventListener('focus', handlePrefetch, {once: true})

    return () => {
      buttonElement.removeEventListener('mouseenter', handlePrefetch)
      buttonElement.removeEventListener('focus', handlePrefetch)
    }
  }, [queryClient, agentsButtonId])

  // Until Primer React Tooltip bug is fixed, restores the tooltip functionality
  // https://github.com/github/primer/issues/5518
  useEffect(() => {
    if (!open) {
      const tooltip = anchorRef.current?.nextElementSibling
      if (tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.removeAttribute('style')
      }
    }
  }, [open, anchorRef])

  // Fetch the copilot chat payload for the provider
  const chatPayload = useCopilotChatPayloadQuery()

  return (
    <ConsecutiveCAPI401sProvider>
      <AnchoredOverlay
        width="large"
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        align="end"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        anchorRef={anchorRef}
        renderAnchor={props.renderAnchor}
        preventOverflow={false}
        focusZoneSettings={{disabled: true}}
      >
        {chatPayload ? (
          <CopilotChatProvider
            threadId={null}
            workerPath={chatPayload.findFileWorkerPath}
            refs={chatPayload.refs ?? []}
            mode="assistive"
            ssoOrganizations={chatPayload.ssoOrganizations ?? []}
            copilotChatPayload={chatPayload}
          >
            {repository ? (
              <CurrentRepositoryProvider repository={repository}>
                <GlobalCopilotOverlay onClose={handleClose} />
              </CurrentRepositoryProvider>
            ) : (
              <GlobalCopilotOverlay onClose={handleClose} />
            )}
          </CopilotChatProvider>
        ) : null}
      </AnchoredOverlay>
      <CCAPopover showPopover={showPopover} dismissPopover={dismissCCAPopover} />
    </ConsecutiveCAPI401sProvider>
  )
}

try{ GlobalAgentsMenuWithAnchor.displayName ||= 'GlobalAgentsMenuWithAnchor' } catch {}
try{ GlobalAgentsMenu.displayName ||= 'GlobalAgentsMenu' } catch {}
try{ GlobalAgentsMenuOverlay.displayName ||= 'GlobalAgentsMenuOverlay' } catch {}